import React, { useEffect, useState } from "react";
import { RefreshCw, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function WeeklyPlansManager() {
    const [data, setData] = useState({ plans: [], generated_at: null, iso_week: null });
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [msg, setMsg] = useState(null);
    const [error, setError] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/weekly-plans");
            setData(data);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const runRefresh = async () => {
        setRefreshing(true);
        setMsg(null);
        setError("");
        try {
            const { data } = await api.post("/admin/weekly-plans/refresh");
            setMsg(`Agenda regenerada: ${data.generated} planes (semana ${data.iso_week}).`);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <div data-testid="admin-weekly-plans">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <div className="text-xs uppercase tracking-widest text-[#082050]/70">Administración</div>
                    <h1 className="mt-2 text-3xl font-semibold text-[#082050] tracking-tight">
                        Qué hacer en Tarifa esta semana
                    </h1>
                    <p className="mt-2 text-sm text-[#4A5568]">
                        Agenda semanal generada automáticamente cada lunes. Semana actual:{" "}
                        <span className="font-medium text-[#082050]">{data.iso_week || "—"}</span>
                    </p>
                </div>
                <button
                    onClick={runRefresh}
                    disabled={refreshing}
                    data-testid="btn-refresh-weekly-plans"
                    className="bg-[#082050] text-white px-5 py-3 text-sm font-medium inline-flex items-center gap-2 hover:bg-[#082050]/90 disabled:opacity-50"
                >
                    <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
                    {refreshing ? "Generando..." : "Regenerar agenda"}
                </button>
            </div>

            {msg && (
                <div className="mb-6 text-sm text-[#082050] border border-[#082050] bg-[#082050]/5 px-4 py-3 inline-flex items-center gap-2">
                    <Sparkles size={14} /> {msg}
                </div>
            )}
            {error && (
                <div className="mb-6 text-sm text-red-600 border border-red-500 bg-red-50 px-4 py-3">
                    {error}
                </div>
            )}

            {loading && <div className="text-[#4A5568]">Cargando...</div>}
            {!loading && (!data.plans || data.plans.length === 0) && (
                <div className="text-[#4A5568] bg-white border border-[#E2E4E9] p-8 text-center">
                    Sin agenda generada. Pulsa "Regenerar agenda" para crear la de esta semana.
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.plans && data.plans.map((p, i) => (
                    <article
                        key={i}
                        data-testid={`admin-weekly-plan-${i}`}
                        className="bg-white border border-[#E2E4E9] p-5"
                    >
                        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[#082050]/60">
                            <span>{p.category}</span>
                            <span>{p.date_label}</span>
                        </div>
                        <h3 className="mt-2 text-base font-semibold text-[#082050]">
                            {p.emoji ? `${p.emoji} ` : ""}{p.title}
                        </h3>
                        <p className="mt-2 text-sm text-[#4A5568] leading-relaxed">{p.description}</p>
                    </article>
                ))}
            </div>
        </div>
    );
}
